import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import type { ExtensionHookInputField } from "@api-client";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import {
  clearPeriodLock,
  getCloseReviews,
  getPeriodLock,
  listEntityReportLocations,
  listExtensionInstallations,
  postCloseReviews,
  reportLocationLabel,
  setPeriodLock,
  type CloseReview,
  type EntityReportLocation,
  type ExtensionInstallation,
  type PeriodLock,
} from "@/lib/api";
import { consoleExtensionsUrl, consoleRolesUrl } from "@/lib/platform-signin";
import { useOrganization } from "@/lib/organization";
import { closeInputWorkflow } from "../close/close-input-workflows";
import { CultivationCapabilityNotice } from "../cultivation/CultivationCapabilityNotice";
import { useCultivationCapabilities } from "../cultivation/cultivation-capabilities";
import { localNavigationPath } from "../extensions/extension-navigation";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

type SetLockInput = Parameters<typeof setPeriodLock>[0];

const CLOSE_HOOK_POINTS = new Set([
  "accounting.period.pre_lock",
  "accounting.period.costing",
]);

type CloseInputValue = string | boolean;

interface InstallationCloseInputs {
  fields: ExtensionHookInputField[];
  conflict: string | null;
}

function installationCloseInputs(
  installation: ExtensionInstallation,
): InstallationCloseInputs {
  const byName = new Map<string, ExtensionHookInputField>();
  let conflict: string | null = null;
  for (const hook of installation.hooks ?? []) {
    if (!CLOSE_HOOK_POINTS.has(hook.point)) continue;
    for (const field of hook.inputs ?? []) {
      const existing = byName.get(field.name);
      if (!existing) {
        byName.set(field.name, field);
        continue;
      }
      if (existing.kind !== field.kind) {
        conflict ??=
          `Close input "${existing.label}" for ${installation.publisher}/${installation.key} ` +
          `is declared with conflicting types (${existing.kind} and ${field.kind}). ` +
          "Ask the extension publisher to fix the manifest before locking.";
        continue;
      }
      if (field.required && !existing.required) {
        byName.set(field.name, { ...existing, required: true });
      }
    }
  }
  return { fields: [...byName.values()], conflict };
}

function buildTypedCloseInputs(
  fields: ExtensionHookInputField[],
  values: Record<string, CloseInputValue> | undefined,
): { value: Record<string, unknown>; errors: Record<string, string> } {
  const value: Record<string, unknown> = {};
  const errors: Record<string, string> = {};
  for (const field of fields) {
    const raw = values?.[field.name];
    if (field.kind === "boolean") {
      const checked = raw === true;
      if (field.required && !checked) {
        errors[field.name] = `"${field.label}" must be checked.`;
      } else if (field.required || raw !== undefined) {
        value[field.name] = checked;
      }
      continue;
    }
    const text = typeof raw === "string" ? raw.trim() : "";
    if (!text) {
      if (field.required) errors[field.name] = `"${field.label}" is required.`;
      continue;
    }
    if (field.kind === "number") {
      const parsed = Number(text);
      if (!Number.isFinite(parsed)) {
        errors[field.name] = `"${field.label}" must be a valid number.`;
        continue;
      }
      value[field.name] = parsed;
      continue;
    }
    value[field.name] = text;
  }
  return { value, errors };
}

function CloseInputField({
  installation,
  field,
  value,
  error,
  disabled,
  onChange,
}: {
  installation: ExtensionInstallation;
  field: ExtensionHookInputField;
  value: CloseInputValue | undefined;
  error: string | undefined;
  disabled: boolean;
  onChange: (value: CloseInputValue) => void;
}) {
  const id = `close-input-${installation.id}-${field.name}`;
  const helpId = field.help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy =
    [helpId, errorId]
      .filter((item): item is string => Boolean(item))
      .join(" ") || undefined;
  return (
    <label htmlFor={id} className="grid gap-1">
      <span className="text-xs font-medium">
        {field.label}
        {field.required ? (
          <>
            <span aria-hidden="true" className="text-[var(--danger)]">
              {" "}
              *
            </span>
            <span className="sr-only"> (required)</span>
          </>
        ) : null}
      </span>
      {field.kind === "boolean" ? (
        <span className="flex items-center gap-2 text-sm">
          <input
            id={id}
            type="checkbox"
            checked={value === true}
            onChange={(event) => onChange(event.target.checked)}
            disabled={disabled}
            aria-required={field.required || undefined}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
          />
          {field.help ? (
            <span
              id={helpId}
              className="text-xs text-[var(--muted-foreground)]"
            >
              {field.help}
            </span>
          ) : null}
        </span>
      ) : field.kind === "select" ? (
        <select
          id={id}
          className="min-h-10 rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 text-sm"
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          aria-required={field.required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
        >
          <option value="">{field.required ? "Select…" : "None"}</option>
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <Input
          id={id}
          type={
            field.kind === "number"
              ? "number"
              : field.kind === "date"
                ? "date"
                : "text"
          }
          step={field.kind === "number" ? "any" : undefined}
          value={typeof value === "string" ? value : ""}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          aria-required={field.required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
        />
      )}
      {field.kind !== "boolean" && field.help ? (
        <span id={helpId} className="text-xs text-[var(--muted-foreground)]">
          {field.help}
        </span>
      ) : null}
      {error ? (
        <span
          id={errorId}
          role="alert"
          className="text-xs text-[var(--danger)]"
        >
          {error}
        </span>
      ) : null}
    </label>
  );
}

function closeStateTone(
  correction: boolean,
  failed: boolean,
  posted: boolean,
  posting: boolean,
): "success" | "danger" | "neutral" | "warning" {
  if (correction || failed) return "danger";
  if (posted) return "success";
  if (posting) return "warning";
  return "neutral";
}

export function PeriodLocksSettings() {
  const { scope } = useOrganization();
  const capabilityState = useCultivationCapabilities(
    scope?.legal_entity_id ?? null,
  );
  const canPost = capabilityState.capabilities?.post ?? true;
  const canLock = capabilityState.capabilities?.lock ?? true;
  const consoleUrl = scope ? consoleExtensionsUrl(scope.organization_id) : null;
  const rolesUrl = scope ? consoleRolesUrl(scope.organization_id) : null;
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [lock, setLock] = useState<PeriodLock | null>(null);
  const [lockLoading, setLockLoading] = useState(true);
  const [lockError, setLockError] = useState<string | null>(null);
  const [lockDate, setLockDate] = useState("");
  const [advisorLockDate, setAdvisorLockDate] = useState("");
  const [savingLock, setSavingLock] = useState(false);
  const [clearingLock, setClearingLock] = useState(false);
  const [pendingClearLock, setPendingClearLock] = useState(false);
  const [pendingLock, setPendingLock] = useState<SetLockInput | null>(null);
  const [closeInstallations, setCloseInstallations] = useState<
    ExtensionInstallation[]
  >([]);
  const [closeLicenses, setCloseLicenses] = useState<EntityReportLocation[]>(
    [],
  );
  const [closeMetadataLoading, setCloseMetadataLoading] = useState(false);
  const [closeMetadataError, setCloseMetadataError] = useState<string | null>(
    null,
  );
  const [closeMetadataVersion, setCloseMetadataVersion] = useState(0);
  const [closeLicenseId, setCloseLicenseId] = useState("");
  const [closePeriodStart, setClosePeriodStart] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [closeInputText, setCloseInputText] = useState<Record<string, string>>(
    {},
  );
  const [closeInputValues, setCloseInputValues] = useState<
    Record<string, Record<string, CloseInputValue>>
  >({});
  const [closeFieldErrors, setCloseFieldErrors] = useState<
    Record<string, Record<string, string>>
  >({});
  const [savedReviews, setSavedReviews] = useState<Record<string, CloseReview>>(
    {},
  );
  const [reviewAction, setReviewAction] = useState<"saving" | "posting" | null>(
    null,
  );

  const reloadLock = useCallback(() => {
    setLockLoading(true);
    setLockError(null);
    getPeriodLock()
      .then((result) => {
        setLock(result);
        if (typeof result.lock_date === "string")
          setLockDate(result.lock_date.slice(0, 10));
      })
      .catch((cause: unknown) => setLockError(toErrorMessage(cause)))
      .finally(() => setLockLoading(false));
  }, []);

  useEffect(() => {
    reloadLock();
  }, [reloadLock]);

  const reloadCloseReviews = useCallback(async () => {
    if (
      !scope?.legal_entity_id ||
      !closeLicenseId ||
      !closePeriodStart ||
      !lockDate
    ) {
      setSavedReviews({});
      return;
    }
    try {
      const result = await getCloseReviews({
        legal_entity_id: scope.legal_entity_id,
        license_id: closeLicenseId,
        period_start: closePeriodStart,
        period_end: lockDate,
      });
      setSavedReviews(
        Object.fromEntries(
          result.reviews.map((review) => [review.installation_id, review]),
        ),
      );
    } catch {
      setSavedReviews({});
    }
  }, [closeLicenseId, closePeriodStart, lockDate, scope?.legal_entity_id]);

  useEffect(() => {
    if (!lockDate) return;
    setClosePeriodStart((current) =>
      current && current <= lockDate ? current : `${lockDate.slice(0, 7)}-01`,
    );
  }, [lockDate]);

  useEffect(() => {
    void reloadCloseReviews();
  }, [reloadCloseReviews]);

  const reviewing = Object.values(savedReviews).some(
    (review) => review.state === "posting",
  );
  useEffect(() => {
    if (!reviewing) return;
    const timer = window.setInterval(() => {
      void reloadCloseReviews();
    }, 2000);
    return () => window.clearInterval(timer);
  }, [reloadCloseReviews, reviewing]);

  useEffect(() => {
    if (!scope?.organization_id || !scope.legal_entity_id) {
      setCloseInstallations([]);
      setCloseLicenses([]);
      setCloseMetadataLoading(false);
      setCloseMetadataError(null);
      return;
    }
    let cancelled = false;
    setCloseMetadataLoading(true);
    setCloseMetadataError(null);
    Promise.all([
      listExtensionInstallations(scope.organization_id),
      listEntityReportLocations(scope.organization_id, scope.legal_entity_id),
    ])
      .then(([installations, locations]) => {
        if (cancelled) return;
        const enabled = installations.filter(
          (item) => item.state === "enabled",
        );
        setCloseInstallations(
          enabled.filter(
            (item) =>
              Boolean(closeInputWorkflow(item, enabled)) ||
              (item.hooks ?? []).some((hook) =>
                CLOSE_HOOK_POINTS.has(hook.point),
              ),
          ),
        );
        setCloseLicenses(locations);
        setCloseLicenseId((current) =>
          locations.some((location) => location.id === current)
            ? current
            : locations.length === 1
              ? locations[0].id
              : "",
        );
      })
      .catch((cause: unknown) => {
        if (!cancelled) setCloseMetadataError(toErrorMessage(cause));
      })
      .finally(() => {
        if (!cancelled) setCloseMetadataLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scope, closeMetadataVersion]);

  const setCloseInputValue = (
    installationId: string,
    name: string,
    value: CloseInputValue,
  ) => {
    setCloseInputValues((current) => ({
      ...current,
      [installationId]: { ...(current[installationId] ?? {}), [name]: value },
    }));
    setCloseFieldErrors((current) => {
      if (!current[installationId]?.[name]) return current;
      const nextInstallation = { ...current[installationId] };
      delete nextInstallation[name];
      return { ...current, [installationId]: nextInstallation };
    });
  };

  const workflowInstallations = closeInstallations.filter((installation) =>
    closeInputWorkflow(installation, closeInstallations),
  );
  const otherInstallations = closeInstallations.filter(
    (installation) => !closeInputWorkflow(installation, closeInstallations),
  );
  const durableClose = workflowInstallations.length > 0;
  const savedWorkflowReviews = workflowInstallations.map(
    (installation) => savedReviews[installation.id] ?? null,
  );
  const allReviewsSaved = savedWorkflowReviews.every(
    (review) =>
      review &&
      review.state !== "failed" &&
      review.state !== "correction_required",
  );
  const allReviewsPosted = savedWorkflowReviews.every(
    (review) =>
      review && (review.state === "posted" || review.state === "locked"),
  );
  const allReviewsApproved = savedWorkflowReviews.every(
    (review) =>
      review &&
      (!review.approval_required || review.approval_state === "approved"),
  );
  const approvalBlocked = savedWorkflowReviews.some(
    (review) =>
      review?.approval_required && review.approval_state !== "approved",
  );
  const approvalReasons = [
    ...new Set(
      savedWorkflowReviews.flatMap((review) =>
        review?.approval_required ? (review.approval_reasons ?? []) : [],
      ),
    ),
  ];
  const hasPosting = savedWorkflowReviews.some(
    (review) => review?.state === "posting",
  );
  const hasFailedReview = savedWorkflowReviews.some(
    (review) => review?.state === "failed",
  );
  const hasCorrection = savedWorkflowReviews.some(
    (review) => review?.state === "correction_required",
  );
  const savedReviewList = savedWorkflowReviews.filter(
    (review): review is CloseReview => review !== null,
  );
  const latestReview =
    [...savedReviewList].sort((left, right) =>
      String(right.reviewed_at ?? "").localeCompare(
        String(left.reviewed_at ?? ""),
      ),
    )[0] ?? null;

  const postReviewedInputs = async () => {
    if (!scope?.legal_entity_id) {
      setError("The books context is unavailable.");
      return;
    }
    if (!durableClose || !closeLicenseId || !closePeriodStart || !lockDate) {
      setError(
        "Choose the location and complete the close period before posting.",
      );
      return;
    }
    if (!allReviewsSaved) {
      setError("Save every reviewed extension input before posting.");
      return;
    }
    if (!canPost) {
      setError(
        "Posting reviewed journals requires the cultivation.post permission.",
      );
      return;
    }
    if (!allReviewsApproved) {
      setError("The saved review still needs checker approval before posting.");
      return;
    }
    setReviewAction("posting");
    setError(null);
    setNotice(null);
    try {
      const result = await postCloseReviews({
        legal_entity_id: scope.legal_entity_id,
        license_id: closeLicenseId,
        period_start: closePeriodStart,
        period_end: lockDate,
      });
      if ("reviews" in result) {
        setSavedReviews(
          Object.fromEntries(
            result.reviews.map((review) => [review.installation_id, review]),
          ),
        );
        setNotice(
          "Reviewed journals are posted. Lock the period when you are ready.",
        );
      } else {
        setNotice(
          "Posting reviewed journals. This update will finish before the lock is available.",
        );
        await reloadCloseReviews();
      }
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setReviewAction(null);
    }
  };

  const submitLock = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setNotice(null);
    try {
      if (!canLock)
        throw new Error(
          "Locking the period requires the cultivation.lock permission.",
        );
      if (!lockDate) throw new Error("Lock date is required.");
      if (closeMetadataLoading)
        throw new Error(
          "Wait for extension close requirements to finish loading.",
        );
      if (closeMetadataError)
        throw new Error(
          "Resolve the extension and license loading error before locking.",
        );
      let extensionClose:
        | {
            license_id: string;
            period_start: string;
            close_inputs: Record<string, unknown>;
          }
        | undefined;
      let closeReview: { license_id: string; period_start: string } | undefined;
      if (closeInstallations.length > 0) {
        if (!closeLicenseId)
          throw new Error("Select the location for the reviewed close inputs.");
        if (!closePeriodStart)
          throw new Error(
            "Period start is required when close extensions are enabled.",
          );
        if (closePeriodStart > lockDate)
          throw new Error("Period start must be on or before the lock date.");
        if (durableClose) {
          if (!allReviewsPosted) {
            throw new Error(
              hasPosting
                ? "Reviewed journals are still posting. Wait for them to finish before locking."
                : "Post reviewed journals before locking the period.",
            );
          }
          closeReview = {
            license_id: closeLicenseId,
            period_start: closePeriodStart,
          };
        }
        const closeInputs: Record<string, unknown> = {};
        const fieldErrors: Record<string, Record<string, string>> = {};
        let blocked: string | null = null;
        for (const installation of otherInstallations) {
          const override = closeInputText[installation.id]?.trim() ?? "";
          if (override) {
            let parsed: unknown;
            try {
              parsed = JSON.parse(override);
            } catch {
              blocked ??= `Close inputs for ${installation.publisher}/${installation.key} must be valid JSON.`;
              continue;
            }
            if (
              !parsed ||
              typeof parsed !== "object" ||
              Array.isArray(parsed)
            ) {
              blocked ??= `Close inputs for ${installation.publisher}/${installation.key} must be a JSON object.`;
              continue;
            }
            closeInputs[installation.id] = parsed;
            continue;
          }
          const { fields, conflict } = installationCloseInputs(installation);
          if (conflict) {
            blocked ??= conflict;
            continue;
          }
          const { value, errors } = buildTypedCloseInputs(
            fields,
            closeInputValues[installation.id],
          );
          if (Object.keys(errors).length > 0) {
            fieldErrors[installation.id] = errors;
            blocked ??= `${Object.values(errors)[0]} (${installation.publisher}/${installation.key})`;
            continue;
          }
          if (Object.keys(value).length > 0) {
            closeInputs[installation.id] = value;
          }
        }
        if (blocked) {
          setCloseFieldErrors(fieldErrors);
          setError(`${blocked} Correct the close inputs below.`);
          return;
        }
        setCloseFieldErrors({});
        if (Object.keys(closeInputs).length > 0) {
          extensionClose = {
            license_id: closeLicenseId,
            period_start: closePeriodStart,
            close_inputs: closeInputs,
          };
        }
      }
      setPendingLock({
        lock_date: lockDate,
        ...(advisorLockDate ? { advisor_lock_date: advisorLockDate } : {}),
        ...(extensionClose ? { extension_close: extensionClose } : {}),
        ...(closeReview ? { close_review: closeReview } : {}),
      });
    } catch (cause) {
      setError(toErrorMessage(cause));
    }
  };

  const confirmLock = async () => {
    if (!pendingLock) return;
    setSavingLock(true);
    setError(null);
    setNotice(null);
    try {
      const result = await setPeriodLock(pendingLock);
      if ("code" in result && result.code === "close_in_progress") {
        setLock({ locked: false });
        setError(
          "Costing or posting decisions are still in progress. The period remains unlocked; review them and retry the lock.",
        );
        reloadLock();
        return;
      }
      setLock(result);
      setNotice(
        `Period locked: every posting on or before ${pendingLock.lock_date} will be rejected with a 409.`,
      );
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setSavingLock(false);
      setPendingLock(null);
    }
  };

  const requestClearLock = () => {
    setPendingClearLock(true);
  };

  const confirmClearLock = async () => {
    if (!canLock) {
      setError(
        "Clearing the period lock requires the cultivation.lock permission.",
      );
      setPendingClearLock(false);
      return;
    }
    setClearingLock(true);
    setError(null);
    setNotice(null);
    try {
      const result = await clearPeriodLock();
      setLock(result);
      setLockDate("");
      setAdvisorLockDate("");
      setNotice("Period lock cleared — all dates are postable again.");
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setClearingLock(false);
      setPendingClearLock(false);
    }
  };

  const selectedLicense = closeLicenses.find(
    (license) => license.id === closeLicenseId,
  );
  const closeWorkspaceHref = scope
    ? `${localNavigationPath("/extensions/sylviq/niche-cultivation-costing/close", scope)}${
        closeLicenseId
          ? `&license_id=${encodeURIComponent(closeLicenseId)}`
          : ""
      }${closePeriodStart ? `&period_start=${encodeURIComponent(closePeriodStart)}` : ""}${
        lockDate ? `&period_end=${encodeURIComponent(lockDate)}` : ""
      }`
    : "/extensions/sylviq/niche-cultivation-costing/close";
  const importsWorkspaceHref = scope
    ? localNavigationPath(
        "/extensions/sylviq/niche-cultivation-costing/imports",
        scope,
      )
    : "/extensions/sylviq/niche-cultivation-costing/imports";
  const historyWorkspaceHref = scope
    ? localNavigationPath(
        "/extensions/sylviq/niche-cultivation-costing/history",
        scope,
      )
    : "/extensions/sylviq/niche-cultivation-costing/history";

  const closeState = !durableClose
    ? otherInstallations.length > 0
      ? "Inputs required"
      : "Not configured"
    : hasCorrection
      ? "Correction required"
      : hasFailedReview
        ? "Posting failed"
        : allReviewsPosted
          ? "Posted"
          : hasPosting
            ? "Posting"
            : allReviewsSaved
              ? "Reviewed"
              : "Not reviewed";
  const closeStateDetail = !durableClose
    ? otherInstallations.length > 0
      ? "This extension requires close inputs. Complete them below before locking."
      : "No close workflow is configured."
    : hasCorrection
      ? "The previous posting was reversed. Open the review, correct the inputs, and save a new revision."
      : hasFailedReview
        ? "Posting failed. Open the review for the error, then retry."
        : allReviewsPosted
          ? "Journals are posted. Confirm them in History, then lock the period."
          : hasPosting
            ? "Costing and posting are running. This page updates automatically."
            : allReviewsSaved
              ? approvalBlocked
                ? "Reviewed inputs are saved. Checker approval is still required before posting."
                : "Reviewed inputs are saved. Post the journals before locking."
              : "Open the close review to complete and save the inputs.";

  return (
    <div className="grid min-w-0 grid-cols-1 gap-6">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">Period locks</h2>
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          Stop new postings through a date. Close inputs stay in the cultivation
          workspace; fiscal years are managed in{" "}
          <Link
            to="/settings"
            search={{ tab: "books", section: "fiscal-years" }}
            className="underline underline-offset-4"
          >
            Books settings
          </Link>
          .
        </p>
      </div>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          {error.includes("Costing or posting decisions") ||
          error.includes("costing and postings must finish") ? (
            <p className="mt-2">
              Extension postings are applied automatically. Retry the lock after
              the costing run finishes.
              {consoleUrl ? (
                <>
                  {" · "}
                  <a className="underline underline-offset-4" href={consoleUrl}>
                    Extension settings in SYLVIQ Console
                  </a>
                </>
              ) : null}
            </p>
          ) : null}
          {error.includes("No Console organization context") ? (
            <p className="mt-1 text-[var(--muted-foreground)]">
              Accounting follows the organization chosen in SYLVIQ Console. Pick
              one there and relaunch Accounting.
            </p>
          ) : null}
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      {closeInstallations.length > 0 ||
      closeMetadataLoading ||
      closeMetadataError ? (
        <section
          aria-labelledby="close-status"
          className="surface grid gap-4 rounded-2xl p-5"
        >
          <div className="flex flex-wrap items-start gap-3">
            <div className="grid gap-1">
              <h3
                id="close-status"
                className="text-lg font-semibold tracking-tight"
              >
                Close status
              </h3>
              <p className="text-sm text-[var(--muted-foreground)]">
                {closeMetadataLoading
                  ? "Checking enabled extensions and locations…"
                  : closeMetadataError
                    ? "Close status is unavailable."
                    : closeStateDetail}
              </p>
            </div>
            {!closeMetadataLoading && !closeMetadataError ? (
              <Badge
                className="ms-auto"
                tone={
                  !durableClose
                    ? "warning"
                    : closeStateTone(
                        hasCorrection,
                        hasFailedReview,
                        allReviewsPosted,
                        hasPosting,
                      )
                }
              >
                {closeState}
              </Badge>
            ) : null}
          </div>

          {closeMetadataError ? (
            <div
              role="alert"
              className="grid gap-2 text-sm text-[var(--danger)]"
            >
              <p>
                Could not load enabled extensions and locations:{" "}
                {closeMetadataError}
              </p>
              <div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    setCloseMetadataVersion((version) => version + 1)
                  }
                >
                  Retry
                </Button>
              </div>
            </div>
          ) : null}

          {!closeMetadataLoading &&
          !closeMetadataError &&
          closeInstallations.length > 0 ? (
            <>
              <dl className="grid gap-3 text-sm sm:grid-cols-3">
                <div className="grid gap-1">
                  <dt className="text-xs font-medium text-[var(--muted-foreground)]">
                    Location
                  </dt>
                  <dd>
                    {selectedLicense
                      ? reportLocationLabel(selectedLicense, closeLicenses)
                      : closeLicenses.length === 0
                        ? "No licensed location"
                        : "Not selected"}
                  </dd>
                </div>
                <div className="grid gap-1">
                  <dt className="text-xs font-medium text-[var(--muted-foreground)]">
                    Period
                  </dt>
                  <dd>
                    {closePeriodStart || "Not set"} to {lockDate || "Not set"}
                  </dd>
                </div>
                <div className="grid gap-1">
                  <dt className="text-xs font-medium text-[var(--muted-foreground)]">
                    Last review
                  </dt>
                  <dd>
                    {latestReview
                      ? `${String(latestReview.reviewed_at ?? "").slice(0, 10)} · ${
                          latestReview.reviewed_by_user_id
                            ? String(latestReview.reviewed_by_user_id).slice(
                                0,
                                8,
                              )
                            : "unknown"
                        }${latestReview.review_revision ? ` · revision ${latestReview.review_revision}` : ""}`
                      : "None saved"}
                  </dd>
                </div>
              </dl>

              {closeLicenses.length === 0 ? (
                <p className="text-sm text-[var(--warning)]">
                  This entity has no location with a license. Retrieve
                  facilities before closing.
                </p>
              ) : null}

              {approvalBlocked ? (
                <p className="text-sm text-[var(--warning)]">
                  Checker approval is still required
                  {savedWorkflowReviews.some(
                    (review) => review?.approval_state === "pending",
                  )
                    ? " (pending)"
                    : ""}
                  . Posting stays blocked until the same reviewed revision is
                  approved.
                  {approvalReasons.length > 0
                    ? ` ${approvalReasons.join(" · ")}`
                    : ""}
                </p>
              ) : null}

              {savedWorkflowReviews.some((review) => review?.last_error) ? (
                <p role="alert" className="text-sm text-[var(--danger)]">
                  {savedWorkflowReviews
                    .map((review) => review?.last_error)
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              ) : null}

              {!canPost ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  Posting requires{" "}
                  <code className="font-mono">cultivation.post</code>.{" "}
                  {rolesUrl ? (
                    <a className="underline underline-offset-4" href={rolesUrl}>
                      Console roles
                    </a>
                  ) : (
                    "Ask an owner in Console roles."
                  )}
                </p>
              ) : null}

              <div className="flex flex-wrap gap-2">
                <Button asChild variant="secondary">
                  <a href={closeWorkspaceHref}>
                    {allReviewsSaved
                      ? "Open saved review"
                      : "Open close review"}
                  </a>
                </Button>
                {durableClose ? (
                  <Button
                    type="button"
                    onClick={() => void postReviewedInputs()}
                    disabled={
                      !allReviewsSaved ||
                      !allReviewsApproved ||
                      !canPost ||
                      allReviewsPosted ||
                      hasPosting ||
                      reviewAction !== null ||
                      savingLock
                    }
                  >
                    {reviewAction === "posting"
                      ? "Posting…"
                      : allReviewsPosted
                        ? "Posted"
                        : "Post reviewed journals"}
                  </Button>
                ) : null}
                <Button asChild variant="outline">
                  <a href={importsWorkspaceHref}>Open imports</a>
                </Button>
                <Button asChild variant="ghost">
                  <a href={historyWorkspaceHref}>History</a>
                </Button>
              </div>

              {otherInstallations.length > 0 ? (
                <details className="rounded-lg border border-[var(--border)] px-3 py-2">
                  <summary className="cursor-pointer text-sm font-semibold">
                    Other close inputs
                  </summary>
                  <div className="mt-3 grid gap-3">
                    {otherInstallations.map((installation) => {
                      const { fields, conflict } =
                        installationCloseInputs(installation);
                      const values = closeInputValues[installation.id] ?? {};
                      const fieldErrors =
                        closeFieldErrors[installation.id] ?? {};
                      return (
                        <fieldset
                          key={installation.id}
                          className="grid gap-3 rounded-xl border border-[var(--border)] p-4"
                        >
                          <legend className="px-1 text-sm font-semibold">
                            {installation.publisher}/{installation.key}
                          </legend>
                          {conflict ? (
                            <p
                              role="alert"
                              className="text-sm text-[var(--danger)]"
                            >
                              {conflict}
                            </p>
                          ) : null}
                          {fields.length === 0 ? (
                            <p className="text-xs text-[var(--muted-foreground)]">
                              This extension does not declare reviewed close
                              inputs.
                            </p>
                          ) : (
                            <div className="grid gap-3 sm:grid-cols-2">
                              {fields.map((field) => (
                                <CloseInputField
                                  key={field.name}
                                  installation={installation}
                                  field={field}
                                  value={values[field.name]}
                                  error={fieldErrors[field.name]}
                                  disabled={savingLock}
                                  onChange={(value) =>
                                    setCloseInputValue(
                                      installation.id,
                                      field.name,
                                      value,
                                    )
                                  }
                                />
                              ))}
                            </div>
                          )}
                          <details className="rounded-lg border border-[var(--border)] px-3 py-2">
                            <summary className="cursor-pointer text-sm font-semibold">
                              Advanced: publisher JSON
                            </summary>
                            <p className="mt-2 text-xs text-[var(--muted-foreground)]">
                              Expert override. A non-empty, valid JSON object
                              replaces the typed fields above for this
                              extension; invalid JSON or a non-object value
                              blocks the lock. Leave it blank to use the typed
                              fields.
                            </p>
                            <label
                              htmlFor={`close-input-${installation.id}`}
                              className="mt-3 grid gap-1 text-xs font-medium"
                            >
                              {installation.publisher}/{installation.key} —
                              reviewed JSON inputs
                              <textarea
                                id={`close-input-${installation.id}`}
                                className="min-h-28 w-full rounded-md border border-[var(--border)] bg-[var(--surface)] p-3 font-mono text-xs"
                                value={closeInputText[installation.id] ?? ""}
                                onChange={(event) =>
                                  setCloseInputText((current) => ({
                                    ...current,
                                    [installation.id]: event.target.value,
                                  }))
                                }
                                placeholder="Paste reviewed JSON for this extension, if required"
                                disabled={savingLock}
                                spellCheck={false}
                              />
                            </label>
                          </details>
                        </fieldset>
                      );
                    })}
                  </div>
                </details>
              ) : null}
            </>
          ) : null}
        </section>
      ) : null}

      <section
        aria-labelledby="lock-status"
        className="surface grid gap-4 rounded-2xl p-5"
      >
        <div className="flex flex-wrap items-start gap-3">
          <div className="grid gap-1">
            <h3
              id="lock-status"
              className="text-lg font-semibold tracking-tight"
            >
              Books lock
            </h3>
            <p className="text-sm text-[var(--muted-foreground)]">
              {lockLoading
                ? "Loading current lock…"
                : lockError
                  ? "Current lock status is unavailable."
                  : lock?.locked
                    ? `Postings on or before ${
                        typeof lock.lock_date === "string"
                          ? lock.lock_date.slice(0, 10)
                          : String(lock.lock_date ?? "")
                      } are rejected with a 409.`
                    : "All dates are currently postable."}
            </p>
          </div>
          <Badge
            className="ms-auto"
            tone={lockError ? "warning" : lock?.locked ? "warning" : "success"}
          >
            {lockLoading
              ? "Loading"
              : lockError
                ? "Unavailable"
                : lock?.locked
                  ? "Locked"
                  : "Unlocked"}
          </Badge>
          {lock?.locked ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={requestClearLock}
              disabled={clearingLock || !canLock}
            >
              {clearingLock ? "Clearing..." : "Clear lock"}
            </Button>
          ) : null}
        </div>

        {lockError ? (
          <div role="alert" className="grid gap-2 text-sm text-[var(--danger)]">
            <p>Could not load the period lock: {lockError}</p>
            <div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={reloadLock}
                disabled={lockLoading}
              >
                {lockLoading ? "Retrying..." : "Retry"}
              </Button>
            </div>
          </div>
        ) : null}

        {!canLock ? (
          <CultivationCapabilityNotice
            capability="lock"
            action="lock or reopen the period"
          />
        ) : null}

        <form onSubmit={submitLock} className="grid gap-4">
          {closeInstallations.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <label
                htmlFor="close-license"
                className="grid gap-1 text-xs font-medium"
              >
                Location
                <select
                  id="close-license"
                  aria-label="Location"
                  className="min-h-10 rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 text-sm"
                  value={closeLicenseId}
                  onChange={(event) => setCloseLicenseId(event.target.value)}
                  disabled={savingLock}
                  required
                >
                  <option value="">Select a location</option>
                  {closeLicenses.map((location) => (
                    <option key={location.id} value={location.id}>
                      {reportLocationLabel(location, closeLicenses)}
                    </option>
                  ))}
                </select>
              </label>
              <label
                htmlFor="close-period-start"
                className="grid gap-1 text-xs font-medium"
              >
                Close period start
                <Input
                  id="close-period-start"
                  type="date"
                  value={closePeriodStart}
                  onChange={(event) => setClosePeriodStart(event.target.value)}
                  disabled={savingLock}
                  required
                />
              </label>
            </div>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2 sm:items-end">
            <label
              htmlFor="lock-date"
              className="grid gap-1 text-xs font-medium"
            >
              Lock date
              <Input
                id="lock-date"
                type="date"
                value={lockDate}
                onChange={(event) => setLockDate(event.target.value)}
                required
                disabled={savingLock}
              />
            </label>
            <details className="rounded-lg border border-[var(--border)] px-3 py-2 text-sm">
              <summary className="cursor-pointer text-xs font-medium">
                Advanced options
              </summary>
              <label
                htmlFor="advisor-lock-date"
                className="mt-3 grid gap-1 text-xs font-medium"
              >
                Advisor lock date{" "}
                <span className="font-normal">(optional)</span>
                <Input
                  id="advisor-lock-date"
                  type="date"
                  value={advisorLockDate}
                  onChange={(event) => setAdvisorLockDate(event.target.value)}
                  disabled={savingLock}
                />
              </label>
            </details>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={
                lockLoading ||
                savingLock ||
                !canLock ||
                closeMetadataLoading ||
                Boolean(closeMetadataError) ||
                (durableClose && !allReviewsPosted)
              }
            >
              {!canLock
                ? "Missing cultivation.lock"
                : durableClose && !allReviewsPosted
                  ? "Waiting for posted journals"
                  : lock?.locked
                    ? "Update lock"
                    : "Lock period"}
            </Button>
          </div>
        </form>
      </section>

      <Dialog
        open={pendingLock !== null}
        onOpenChange={(open) => {
          if (!open && !savingLock) setPendingLock(null);
        }}
        title="Set the period lock?"
        description={
          pendingLock
            ? `This locks the books through ${pendingLock.lock_date}. Postings on or before this date are rejected with a 409 until the lock is cleared or changed.`
            : undefined
        }
      >
        <dl className="grid gap-2 text-sm">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <dt className="font-medium">Lock date</dt>
            <dd className="text-[var(--muted-foreground)]">
              {pendingLock?.lock_date}
            </dd>
          </div>
          <div className="flex flex-wrap items-baseline gap-x-2">
            <dt className="font-medium">Advisor lock date</dt>
            <dd className="text-[var(--muted-foreground)]">
              {pendingLock?.advisor_lock_date ?? "Not set"}
            </dd>
          </div>
          {pendingLock?.close_review ? (
            <div className="flex flex-wrap items-baseline gap-x-2">
              <dt className="font-medium">Close review</dt>
              <dd className="text-[var(--muted-foreground)]">
                {selectedLicense
                  ? reportLocationLabel(selectedLicense, closeLicenses)
                  : "Selected location"}
                , {pendingLock.close_review.period_start} to{" "}
                {pendingLock.lock_date}. Posted state is checked again before
                writing.
              </dd>
            </div>
          ) : null}
          {pendingLock?.extension_close ? (
            <div className="flex flex-wrap items-baseline gap-x-2">
              <dt className="font-medium">Other close inputs</dt>
              <dd className="text-[var(--muted-foreground)]">
                {Object.keys(pendingLock.extension_close.close_inputs).length}{" "}
                extension
                {Object.keys(pendingLock.extension_close.close_inputs)
                  .length === 1
                  ? ""
                  : "s"}{" "}
                included.
              </dd>
            </div>
          ) : null}
        </dl>
        <div className="actions-row mt-4 flex-wrap">
          <Button
            type="button"
            variant="secondary"
            disabled={savingLock}
            onClick={() => setPendingLock(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={savingLock}
            onClick={() => void confirmLock()}
          >
            {savingLock ? "Saving..." : "Set lock"}
          </Button>
        </div>
      </Dialog>

      <Dialog
        open={pendingClearLock}
        onOpenChange={(open) => {
          if (!open && !clearingLock) setPendingClearLock(false);
        }}
        title="Clear the period lock?"
        description="Every date becomes postable again. Only do this if the lock was set in error."
      >
        <div className="actions-row mt-4 flex-wrap">
          <Button
            type="button"
            variant="secondary"
            disabled={clearingLock}
            onClick={() => setPendingClearLock(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={clearingLock}
            onClick={() => void confirmClearLock()}
          >
            {clearingLock ? "Clearing..." : "Clear lock"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
